import { Link, useParams } from 'react-router-dom';

import type { ClientDashboardDto } from '@botguide/shared';
import { formatCents } from '@botguide/shared';

import { Badge, Card, EmptyState, ErrorState, Icon, LoadingScreen, PageHeader, Stat } from '@/components';
import { ApplicationCard } from '@/components/client/ApplicationCard';
import { EMPTY, formatCount, formatInstant, formatRelative } from '@/lib/format';
import { useClientDashboard, useClients } from '@/lib/queries';
import { ROUTES } from '@/lib/routes';
import type { ClientDashboardParams } from '@/lib/screens';
import { useSession } from '@/lib/session';

/*
 * ---------------------------------------------------------------------------
 * The client dashboard. The only screen a client ever sees.
 * ---------------------------------------------------------------------------
 *
 * ## No people, by construction
 *
 * Nothing on this screen names, pictures or counts a person. The API strips
 * assignees before the payload leaves the server, and `ClientDashboardDto`
 * has no person-shaped field anywhere in it - so the only way a name could
 * appear here is if this screen fetched one from somewhere else. It does not:
 * the only queries on this page are `useClientDashboard` and, for the owner's
 * client picker, `useClients`. `Avatar` and `PersonChip` are not imported.
 *
 * ## Two entry points
 *
 * `/dashboard` is the caller's own client. `/dashboard/:clientId` is an owner
 * previewing one. An owner has no client of their own, so `GET
 * /api/client-dashboard` answers them with a 400 telling them to name one -
 * which is correct for the API and useless as a screen. The picker below
 * catches that case before the request is made.
 */

export default function ClientDashboard() {
  const { clientId } = useParams<ClientDashboardParams>();
  const { user } = useSession();

  if (!user) return <LoadingScreen label="Checking your session" />;

  // An owner landing on `/dashboard` has nothing to resolve - they belong to
  // no client - so they choose whose view to look at rather than get a 400.
  if (user.role === 'owner' && !clientId) return <ClientPicker />;

  return <Dashboard clientId={clientId} />;
}

/* -------------------------------------------------------------------------- */
/* The dashboard proper                                                       */
/* -------------------------------------------------------------------------- */

function Dashboard({ clientId }: { clientId: string | undefined }) {
  const { user } = useSession();
  const dashboard = useClientDashboard(clientId);
  const isPreview = user?.role === 'owner';

  if (dashboard.isPending) return <LoadingScreen label="Gathering your delivery status" />;

  if (dashboard.isError) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />
      </>
    );
  }

  const data = dashboard.data;
  const totals = portfolioTotals(data);

  return (
    <>
      <PageHeader
        title={data.client.name}
        description={data.client.summary || 'Everything BotGuide is building for you, and what it is worth.'}
        actions={
          isPreview ? (
            <Link
              to={ROUTES.dashboard}
              className="inline-flex items-center gap-1.5 rounded-md border border-line-strong px-3 py-1.5 text-[0.8125rem] font-medium transition-colors hover:border-accent hover:text-accent-ink"
            >
              <Icon name="chevronLeft" size={13} />
              All clients
            </Link>
          ) : null
        }
        toolbar={
          <div className="flex flex-wrap items-center gap-3">
            {isPreview ? (
              <Badge tone="warn" icon="info">
                Previewing what {data.client.name} sees
              </Badge>
            ) : null}
            <p className="text-[0.75rem] text-subtle" title={formatInstant(data.updatedAt)}>
              Updated {formatRelative(data.updatedAt)}
            </p>
          </div>
        }
      />

      {data.applications.length === 0 ? (
        <EmptyState
          title="Nothing is under way yet"
          icon="compass"
          description="As soon as an application is set up for you, its work and its value will appear here."
        />
      ) : (
        <>
          <Card padding="lg" className="rise mb-6">
            <div className="grid grid-cols-2 gap-x-6 gap-y-6 lg:grid-cols-4">
              {/*
                The cutoff date was removed on 2026-10-16 at Ryan's request.
                The zero case keeps its hint: a client seeing $0 needs to know
                it means "not billed yet" rather than "worth nothing".
              */}
              <Stat
                label="Invested to date"
                value={formatCents(totals.spendCents)}
                hint={totals.spendThrough ? undefined : 'nothing invoiced yet'}
              />
              <Stat
                label="Applications"
                value={data.applications.length}
                hint={formatCount(totals.featureCount, 'feature')}
              />
              <Stat
                label="Being built now"
                value={totals.inProgress}
                hint={totals.inProgress === 1 ? 'feature in progress' : 'features in progress'}
                tone="var(--lane-in_progress)"
              />
              <Stat
                label="Delivered"
                value={totals.completed}
                hint={totals.completed === 1 ? 'feature in use' : 'features in use'}
                tone="var(--lane-completed)"
              />
            </div>

            {totals.blocked > 0 ? (
              <p className="mt-5 flex items-start gap-2 border-t border-line pt-4 text-[0.8125rem] text-warn-ink">
                <Icon name="alert" size={14} className="mt-0.5 shrink-0" />
                <span>
                  {formatCount(totals.blocked, 'item')} across the portfolio {totals.blocked === 1 ? 'is' : 'are'}{' '}
                  blocked and waiting on something before work can continue.
                </span>
              </p>
            ) : null}
          </Card>

          <div className="flex flex-col gap-6">
            {data.applications.map((entry, index) => (
              <ApplicationCard key={entry.application.id} entry={entry} index={index + 1} />
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Portfolio totals                                                           */
/* -------------------------------------------------------------------------- */

interface PortfolioTotals {
  spendCents: number;
  /** The latest cutoff across applications; null when nothing is invoiced. */
  spendThrough: string | null;
  featureCount: number;
  inProgress: number;
  completed: number;
  blocked: number;
}

/**
 * Sums for the band at the top.
 *
 * Spend is added in cents and never divided - `formatCents` is the only thing
 * that turns it into dollars.
 *
 * `spendThrough` is still computed as the LATEST of the per-application
 * cutoffs - a total is only as current as its least current part - but since
 * 2026-10-16 it is no longer displayed. It survives as the test for whether
 * anything has been invoiced at all, which is what decides between a hint
 * saying "nothing invoiced yet" and no hint.
 *
 * There is deliberately no portfolio-level ROI. The applications have
 * different cutoff dates and different benefit estimates; one blended ratio
 * across them would be a number with no defensible meaning.
 */
function portfolioTotals(data: ClientDashboardDto): PortfolioTotals {
  let spendCents = 0;
  let spendThrough: string | null = null;
  let featureCount = 0;
  let inProgress = 0;
  let completed = 0;
  let blocked = 0;

  for (const entry of data.applications) {
    if (entry.spend) {
      spendCents += entry.spend.spendCents;
      const cutoff = entry.spend.spendThrough;
      if (cutoff && entry.spend.spendCents > 0 && (spendThrough === null || cutoff > spendThrough)) {
        spendThrough = cutoff;
      }
    }
    for (const feature of entry.features) {
      featureCount += 1;
      if (feature.rollup.lane === 'in_progress') inProgress += 1;
      if (feature.rollup.lane === 'completed') completed += 1;
      blocked += feature.rollup.blockedCount;
    }
  }

  return { spendCents, spendThrough, featureCount, inProgress, completed, blocked };
}

/* -------------------------------------------------------------------------- */
/* Owner preview picker                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Whose dashboard to look at. Owner only - a client never reaches this,
 * because `/dashboard` resolves to their own client from their session.
 */
function ClientPicker() {
  const clients = useClients();

  if (clients.isPending) return <LoadingScreen label="Loading clients" />;

  if (clients.isError) {
    return (
      <>
        <PageHeader title="Client dashboard" />
        <ErrorState error={clients.error} onRetry={() => void clients.refetch()} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Client dashboard"
        description="Open a client's dashboard to see exactly what they see - work lanes, spend and projected return, and no people."
      />

      {clients.data.length === 0 ? (
        <EmptyState
          title="No clients yet"
          icon="compass"
          description="Add a client under Work items and their dashboard will appear here."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {clients.data.map((client, index) => (
            <Link
              key={client.id}
              to={ROUTES.clientDashboard(client.id)}
              className="rise block rounded-lg focus-visible:outline-none"
              style={{ animationDelay: `${index * 70}ms` }}
            >
              <Card interactive padding="lg" className="h-full">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="font-display text-[1.25rem] font-semibold tracking-tight">
                      {client.name}
                    </h2>
                    <p className="mt-1 font-mono text-[0.75rem] text-subtle">{client.slug}</p>
                  </div>
                  {client.isActive ? null : <Badge tone="neutral">Inactive</Badge>}
                </div>
                <p className="mt-3 text-[0.8125rem] leading-relaxed text-muted">
                  {client.summary || EMPTY}
                </p>
                <p className="mt-4 inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-accent-ink">
                  Open their view
                  <Icon name="chevronRight" size={13} />
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
